import { __decorate } from "tslib";
import { attr, DOM, html, nullableNumberConverter, observable, slotted } from '@microsoft/fast-element';
import { FoundationElement } from '@microsoft/fast-foundation';
import { traverseShadowRootsForElementWithId, inflateRectInDirection, isPointOverRect, removeAllChildren, } from '../common/utils/DomUtil';
import { ChameleonElementMixin } from '../common/mixin';
import { calculatePopoverPosition } from './PopoverContentV2/PopoverContentUtil';
import { forwardKeyboardEvent } from '../Listbox/helpers';
import { createFocusTrap } from 'focus-trap';
import { uniqueId, convertStylePropertyPixelsToNumber } from '@microsoft/fast-web-utilities';
import { DEV } from 'esm-env';
const DEFAULT_Z_INDEX = 1;
const DEFAULT_POSITION = `bottom-center`;
const TRIGGER_MOUSE_MOVE_OFFSET = 20;
const NATIVE_TRIGGER_LIST = [
    'chameleon-button',
    'chameleon-round-button',
    'chameleon-icon-button',
    'chameleon-internal-navigation-rail-item',
    'button',
];
export const popoverV2Template = html `
  <template
    ><div class="popover"><slot ${slotted('defaultSlot')}></slot></div
  ></template>
`;
export class PopoverV2Component extends ChameleonElementMixin(FoundationElement) {
    isOpenChanged(_, next) {
        if (!this.$fastController.isConnected || this.hidden || !this.triggerEl) {
            return;
        }
        if (next) {
            if (this.delay && !this.isTriggeredByFocus) {
                this.timeoutId = setTimeout(() => {
                    this.openPopover();
                }, this.delay);
            }
            else {
                this.openPopover();
            }
        }
        else {
            this.closePopover();
        }
    }
    triggerIdChanged(_, next) {
        console.log({ triggerId: next });
        if (!next || next === '') {
            return;
        }
        // The first time this function is run, the elements might not be actually rendered into the DOM.
        // For this reason, we need to use the queueUpdate here to wait until the element and its triggers
        // are actually rendered into the DOM so we can find it and attach the listeners.
        DOM.queueUpdate(() => {
            this.triggerEl = traverseShadowRootsForElementWithId(next, this);
            if (!this.triggerEl) {
                return;
            }
            this.triggerEl.addEventListener('click', this.triggerClickHandler);
            this.triggerEl.addEventListener('mouseover', this.triggerMouseOverHandler);
            this.triggerEl.addEventListener('keydown', this.handleKeyDown);
            this.triggerEl.addEventListener('focus', this.handleTriggerFocus);
            this.triggerEl.addEventListener('blur', this.handleTriggerBlur);
            this.triggerEl.setAttribute('role', 'button');
            if (this.isTooltip) {
                this.triggerEl.setAttribute('aria-describedby', this.contentElId);
            }
            else {
                this.triggerEl.setAttribute('aria-haspopup', this.ariaHasPopupValue);
                this.triggerEl.setAttribute('aria-expanded', `${this.isOpen}`);
                this.triggerEl.setAttribute('aria-controls', this.contentElId);
                if (this.triggerEl.parentElement) {
                    this.triggerObserver.observe(this.triggerEl.parentElement, { childList: true });
                }
            }
        });
    }
    labelChanged() {
        this.setAccessibleLabel();
    }
    labelIdChanged() {
        this.setAccessibleLabel();
    }
    get isClickTrigger() {
        return this.triggerOn === 'click';
    }
    get isMouseOverTrigger() {
        return this.triggerOn === 'mouseover';
    }
    get ariaHasPopupValue() {
        return this.menu ? 'menu' : 'dialog';
    }
    constructor() {
        super();
        this.position = DEFAULT_POSITION;
        this.compact = false;
        this.inverted = false;
        this.hidden = false;
        this.arrow = false;
        this.menu = false;
        this.triggerOn = 'click';
        this.isOpen = false;
        /**
         * @description The `id` of the element that triggers the popover
         * */
        this.triggerId = '';
        this.defaultSlot = [];
        this.contentEl = document.createElement('chameleon-popover-content-v2');
        this.isTooltip = false;
        this.delay = undefined;
        this.triggerEl = null;
        this.contentElId = uniqueId('popover-content-');
        this.isTriggeredByFocus = false;
        this.triggerClickHandler = () => {
            if (!this.isClickTrigger) {
                return;
            }
            this.isOpen = !this.isOpen;
        };
        this.triggerMouseOverHandler = () => {
            if (!this.isMouseOverTrigger || this.isOpen) {
                return;
            }
            this.isOpen = true;
        };
        this.documentClickHandler = (event) => {
            var _a;
            if (this.isMouseOverTrigger || !this.isOpen) {
                document.removeEventListener('click', this.documentClickHandler);
                return;
            }
            if (((_a = this.triggerEl) === null || _a === void 0 ? void 0 : _a.contains(event.target)) ||
                this.contentEl.contains(event.target) ||
                this.contentEl === event.target) {
                return;
            }
            this.isOpen = false;
            document.removeEventListener('click', this.documentClickHandler);
        };
        this.documentMouseMoveHandler = (event) => {
            var _a, _b;
            const x = event.clientX;
            const y = event.clientY;
            const contentPosition = this.contentEl.position;
            const position = contentPosition === null || contentPosition === void 0 ? void 0 : contentPosition.split('-')[0];
            const additionalOffsetX = (_a = convertStylePropertyPixelsToNumber(this.style, '--additional-offset-x')) !== null && _a !== void 0 ? _a : 0;
            const additionalOffsetY = (_b = convertStylePropertyPixelsToNumber(this.style, '--additional-offset-y')) !== null && _b !== void 0 ? _b : 0;
            const offsetX = Number.isNaN(additionalOffsetX) ? 0 : additionalOffsetX;
            const offsetY = Number.isNaN(additionalOffsetY) ? 0 : additionalOffsetY;
            const offset = position === 'top' || position === 'bottom'
                ? TRIGGER_MOUSE_MOVE_OFFSET + offsetY
                : TRIGGER_MOUSE_MOVE_OFFSET + offsetX;
            const triggerBounds = inflateRectInDirection(this.triggerEl.getBoundingClientRect(), offset, position);
            if (!(isPointOverRect(triggerBounds, { x, y }) || isPointOverRect(this.contentEl.getBoundingClientRect(), { x, y }))) {
                this.isOpen = false;
            }
        };
        this.isCustomTrigger = () => {
            var _a;
            const tagName = (_a = this.triggerEl) === null || _a === void 0 ? void 0 : _a.tagName.toLowerCase();
            return tagName && NATIVE_TRIGGER_LIST.indexOf(tagName) === -1;
        };
        this.handleKeyDown = (event) => {
            if (event.code === 'Space') {
                if (this.isCustomTrigger()) {
                    // Prevents scrolling the page down
                    event.preventDefault();
                }
            }
            if (event.key === 'Enter' || event.code === 'Space') {
                // Chameleon buttons already have a keyboard event hooked up, no need to override them
                if (this.isCustomTrigger()) {
                    this.isOpen = !this.isOpen;
                    document.addEventListener('click', this.documentClickHandler);
                }
            }
        };
        this.handleContentKeydownEvent = (e) => {
            const innerContentElement = this.contentEl.querySelector('chameleon-listbox');
            if (innerContentElement &&
                (e.key === 'ArrowUp' ||
                    e.key === 'ArrowDown' ||
                    e.key === 'ArrowRight' ||
                    e.key === 'ArrowLeft' ||
                    e.key === 'Enter' ||
                    e.key === ' ')) {
                forwardKeyboardEvent(e, innerContentElement);
            }
            if (e.key === 'Escape') {
                this.isOpen = false;
            }
        };
        this.handleTriggerFocus = () => {
            if (this.isClickTrigger) {
                return;
            }
            this.isTriggeredByFocus = true;
            this.isOpen = true;
        };
        this.handleTriggerBlur = () => {
            if (this.isClickTrigger) {
                return;
            }
            this.isOpen = false;
        };
        // This function is called by the MutationObserver whenever some change happens to the
        // contents of the popover element. We want to react to these changes under the following circumstances:
        // - The content element is rendered in the DOM (i.e. the data-rendered attribute is present). If it's not rendered
        //   we don't have to react to content changes as those changes will be picked up the next time the popover is opened and
        //   we copy the elements over the the content element.
        // - Nodes were added to the element. We dont want to react to nodes being removed, because we would
        //   create a loop where we would react to the element itself removing its own children to append them to the content element.
        // - mutation.type is either childList or characterData. childList means child element changes, charaterData means a child text node was changed.
        this.handleContentMutation = (mutationsList) => {
            for (const mutation of mutationsList) {
                if (this.contentEl.hasAttribute('data-rendered') &&
                    mutation.addedNodes.length > 0 &&
                    (mutation.type === 'childList' || mutation.type === 'characterData')) {
                    this.updateContentElText();
                    this.positionPopover();
                }
            }
        };
        this.handleTriggerMutation = (mutationsList) => {
            for (const mutation of mutationsList) {
                const isTriggerlElRemoved = Array.from(mutation.removedNodes).some((node) => node === this.triggerEl);
                if (this.contentEl.hasAttribute('data-rendered') && mutation.type === 'childList' && isTriggerlElRemoved) {
                    this.closePopover();
                }
            }
        };
        this.focusTrap = createFocusTrap(this.contentEl, {
            onDeactivate: () => {
                this.isOpen = false;
            },
            fallbackFocus: this.contentEl,
            allowOutsideClick: true,
            setReturnFocus: () => this.triggerEl,
            tabbableOptions: {
                getShadowRoot: true,
            },
        });
        this.contentObserver = new MutationObserver(this.handleContentMutation);
        this.triggerObserver = new MutationObserver(this.handleTriggerMutation);
    }
    connectedCallback() {
        super.connectedCallback();
        this.contentEl.setAttribute('id', this.contentElId);
        if (this.isOpen) {
            DOM.queueUpdate(() => {
                this.openPopover();
            });
        }
        this.contentObserver.observe(this, { childList: true, subtree: true });
        this.setAccessibleLabel();
    }
    disconnectedCallback() {
        if (!this.triggerEl) {
            return;
        }
        this.triggerEl.removeEventListener('click', this.triggerClickHandler);
        this.triggerEl.removeEventListener('mouseover', this.triggerMouseOverHandler);
        this.triggerEl.removeEventListener('keydown', this.handleKeyDown);
        this.triggerEl.removeEventListener('focus', this.handleTriggerFocus);
        this.triggerEl.removeEventListener('blur', this.handleTriggerBlur);
        document.removeEventListener('click', this.documentClickHandler);
        document.removeEventListener('click', this.documentMouseMoveHandler);
        this.contentObserver.disconnect();
        this.closePopover();
    }
    close() {
        this.isOpen = false;
    }
    positionPopover() {
        var _a;
        (_a = this.cleanupFn) === null || _a === void 0 ? void 0 : _a.call(this);
        calculatePopoverPosition(this, this.contentEl, this.triggerEl).then((cleanup) => {
            this.cleanupFn = cleanup;
        });
    }
    updateContentZIndex() {
        const zIndexAssignedFromCss = +getComputedStyle(this).getPropertyValue('--z-index').trim();
        const zIndexToUse = !!zIndexAssignedFromCss ? zIndexAssignedFromCss : !!this.zIndex ? this.zIndex : DEFAULT_Z_INDEX;
        this.contentEl.setAttribute('z-index', zIndexToUse.toString());
    }
    openPopover() {
        var _a, _b, _c, _d, _e, _f;
        if (!this.triggerEl) {
            return;
        }
        this.contentEl.popoverEl = this;
        this.contentEl.setAttribute('inactive', 'false');
        this.contentEl.setAttribute('arrow', this.arrow.toString());
        this.contentEl.setAttribute('menu', this.menu.toString());
        this.contentEl.setAttribute('compact', this.compact.toString());
        this.contentEl.setAttribute('inverted', this.inverted.toString());
        this.width && this.contentEl.setAttribute('width', (_b = (_a = this.width) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : '');
        this.minWidth && this.contentEl.setAttribute('min-width', (_d = (_c = this.minWidth) === null || _c === void 0 ? void 0 : _c.toString()) !== null && _d !== void 0 ? _d : '');
        this.maxWidth && this.contentEl.setAttribute('max-width', (_f = (_e = this.maxWidth) === null || _e === void 0 ? void 0 : _e.toString()) !== null && _f !== void 0 ? _f : '');
        this.updateContentZIndex();
        DOM.queueUpdate(() => {
            var _a;
            (_a = this.triggerEl) === null || _a === void 0 ? void 0 : _a.setAttribute('aria-expanded', 'true');
        });
        this.updateContentElText();
        this.positionPopover();
        this.addEventListener('keydown', this.handleContentKeydownEvent);
        document.body.appendChild(this.contentEl);
        this.$emit('popoverchange', { isOpen: true }, { bubbles: false });
        // We set this attribute so we have knowledge of when the content is truly rendered.
        // It's used for syncing the content changes of the popover element to the popover-content
        // when it's opened inside the content MutationObserver.
        DOM.queueUpdate(() => {
            this.contentEl.setAttribute('data-rendered', 'true');
        });
        if (!this.isTooltip && !this.isMouseOverTrigger) {
            DOM.queueUpdate(() => {
                this.focusTrap.activate();
            });
        }
        if (this.isTooltip) {
            DOM.queueUpdate(() => {
                document.addEventListener('mousemove', this.documentMouseMoveHandler);
            });
        }
        else {
            DOM.queueUpdate(() => {
                document.addEventListener('click', this.documentClickHandler);
            });
        }
    }
    closePopover() {
        var _a;
        if (!this.triggerEl) {
            return;
        }
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = undefined;
        }
        this.contentEl.removeAttribute('data-rendered');
        this.isTriggeredByFocus = false;
        this.focusTrap.deactivate();
        (_a = this.cleanupFn) === null || _a === void 0 ? void 0 : _a.call(this);
        this.contentEl.remove();
        this.triggerEl.setAttribute('aria-expanded', 'false');
        this.removeEventListener('keydown', this.handleContentKeydownEvent);
        document.removeEventListener('click', this.documentClickHandler);
        if (this.isMouseOverTrigger) {
            document.removeEventListener('mousemove', this.documentMouseMoveHandler);
        }
        this.$emit('popoverchange', { isOpen: false }, { bubbles: false });
    }
    updateContentElText() {
        // If the popover element has childNodes, it means the popover is either being rendered for the first
        // or its contents have changed and we need to reflect those changes in the content element.
        // To do that we remove all existing child nodes of the content element and then copy over the nodes
        // found in the popover element itself.
        if (this.childNodes.length > 0) {
            removeAllChildren(this.contentEl);
        }
        Array.from(this.childNodes).forEach((element) => {
            if (element instanceof HTMLSlotElement) {
                const assignedNodes = element.assignedNodes();
                for (const node of assignedNodes) {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        node.removeAttribute('slot');
                    }
                    this.contentEl.appendChild(node);
                }
            }
            else {
                this.contentEl.appendChild(element);
            }
        });
    }
    setAccessibleLabel() {
        var _a;
        if (this.label) {
            this.contentEl.setAttribute('aria-label', this.label);
            return;
        }
        const labellingElement = document.querySelector(`#${this.labelId}`);
        if (this.labelId && ((_a = labellingElement === null || labellingElement === void 0 ? void 0 : labellingElement.textContent) === null || _a === void 0 ? void 0 : _a.length)) {
            this.contentEl.setAttribute('aria-label', labellingElement === null || labellingElement === void 0 ? void 0 : labellingElement.textContent);
            return;
        }
        if (DEV) {
            this.validateAccessibleLabel();
        }
    }
    validateAccessibleLabel() {
        var _a;
        if (!((_a = this.contentEl.getAttribute('aria-label')) === null || _a === void 0 ? void 0 : _a.length) && !this.outerHTML.includes('chameleon-tooltip-v3')) {
            console.error(`CHAMELEON-A11Y: Popover component must have an accessible label provided by either 
  1) a non-empty label attribute to be used as aria-label 
  2) a visible label whose id is given as the attribute labelId.
`);
        }
    }
}
__decorate([
    attr()
], PopoverV2Component.prototype, "position", void 0);
__decorate([
    attr({ converter: nullableNumberConverter })
], PopoverV2Component.prototype, "width", void 0);
__decorate([
    attr({ attribute: 'min-width', converter: nullableNumberConverter })
], PopoverV2Component.prototype, "minWidth", void 0);
__decorate([
    attr({ attribute: 'max-width', converter: nullableNumberConverter })
], PopoverV2Component.prototype, "maxWidth", void 0);
__decorate([
    attr({ attribute: 'z-index', converter: nullableNumberConverter })
], PopoverV2Component.prototype, "zIndex", void 0);
__decorate([
    attr({ mode: 'boolean' })
], PopoverV2Component.prototype, "compact", void 0);
__decorate([
    attr({ mode: 'boolean' })
], PopoverV2Component.prototype, "inverted", void 0);
__decorate([
    attr({ mode: 'boolean' })
], PopoverV2Component.prototype, "hidden", void 0);
__decorate([
    attr({ mode: 'boolean' })
], PopoverV2Component.prototype, "arrow", void 0);
__decorate([
    attr({ mode: 'boolean' })
], PopoverV2Component.prototype, "menu", void 0);
__decorate([
    attr({ attribute: 'trigger-on' })
], PopoverV2Component.prototype, "triggerOn", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'is-open' })
], PopoverV2Component.prototype, "isOpen", void 0);
__decorate([
    attr({ attribute: 'trigger-id' })
], PopoverV2Component.prototype, "triggerId", void 0);
__decorate([
    attr({ attribute: 'label-id' })
], PopoverV2Component.prototype, "labelId", void 0);
__decorate([
    attr
], PopoverV2Component.prototype, "label", void 0);
__decorate([
    observable
], PopoverV2Component.prototype, "defaultSlot", void 0);
